import { Component, OnInit, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

import { CentroProcedure } from "src/app/_core/models/centros/centro-procedure.model";
import { CentrosService } from "src/app/_core/providers/centros.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { ExcelExportService } from "src/app/_core/services/excel-export.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";
import { getHomeMenuWithVisited } from "src/app/_core/constants/menu.constants";
import { UserProfileStore } from "src/app/_core/stores/user-profile.store";
import { Policies } from "src/app/_core/models/main/policies.enum";

/**
 * Listado de Centros (ui-redesign). Datos reales via /Centros/List (getCentros).
 */
@Component({
	selector: "app-centros-list",
	templateUrl: "./centros-list.page.html",
	styleUrls: ["./centros-list.page.scss"],
	standalone: false,
})
export class CentrosListComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();
	private readonly _menu = getHomeMenuWithVisited("/sites");
	isLoading = false;

	centros: CentroProcedure[] = [];
	filtered: CentroProcedure[] = [];
	viewMode: "table" | "cards" = "table";
	page = 1;
	pageSize = 12;
	searchText = "";
	selectedInstalacion = "";
	selectedActividad = "";
	selectedActivo = "";

	instalacionOptions: string[] = [];
	actividadOptions: string[] = [];

	constructor(
		private readonly titleService: Title,
		private readonly translateService: TranslateService,
		private readonly router: Router,
		private readonly centrosService: CentrosService,
		private readonly notificationService: NotificationService,
		private readonly global: GlobalService,
		private readonly userProfileStore: UserProfileStore,
		private readonly excelExport: ExcelExportService,
	) {
		const miga: MigaDePan[] = [
			{ nombre: "header.menu.sites", url: window.location.href },
		];
		this.global.limpiarMigas();
		this.global.actualizarMigas(miga);
	}

	ngOnInit(): void {
		this.titleService.setTitle(
			this.translateService.instant("header.menu.sites"),
		);
		this.global.setHeaderOptions([], null, [], this._menu);
		this.load();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private load(): void {
		this.isLoading = true;
		this.centrosService
			.getCentros()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					// Orden por defecto alfabético por descripción (determinista).
					this.centros = (data ?? []).sort((a, b) =>
						(a.descripcion || a.codigo || "").localeCompare(
							b.descripcion || b.codigo || "",
							undefined,
							{ sensitivity: "base" },
						),
					);
					this.instalacionOptions = Array.from(
						new Set(
							this.centros
								.map((c) => c.tipo_Instalacion)
								.filter((v) => v && v.trim() !== ""),
						),
					).sort();
					this.actividadOptions = Array.from(
						new Set(
							this.centros
								.map((c) => c.actividadContrato)
								.filter((v): v is string => !!v && v.trim() !== ""),
						),
					).sort();
					this.applyFilters();
					this.isLoading = false;
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los centros",
					);
					this.isLoading = false;
				},
			});
	}

	get hasActiveFilters(): boolean {
		return !!(
			this.searchText?.trim() ||
			this.selectedInstalacion ||
			this.selectedActividad ||
			this.selectedActivo
		);
	}

	onSearchChange(v: string): void {
		this.searchText = v;
		this.applyFilters();
	}
	onInstalacionChange(v: string): void {
		this.selectedInstalacion = v;
		this.applyFilters();
	}
	onActividadChange(v: string): void {
		this.selectedActividad = v;
		this.applyFilters();
	}
	onActivoChange(v: string): void {
		this.selectedActivo = v;
		this.applyFilters();
	}
	clearFilters(): void {
		this.searchText = "";
		this.selectedInstalacion = "";
		this.selectedActividad = "";
		this.selectedActivo = "";
		this.applyFilters();
	}

	private applyFilters(): void {
		let f = [...this.centros];
		if (this.selectedInstalacion) {
			f = f.filter((c) => c.tipo_Instalacion === this.selectedInstalacion);
		}
		if (this.selectedActividad) {
			f = f.filter((c) => c.actividadContrato === this.selectedActividad);
		}
		if (this.selectedActivo) {
			const active = this.selectedActivo === "true";
			f = f.filter((c) => !!c.activo === active);
		}
		if (this.searchText.trim()) {
			const s = this.searchText.toLowerCase();
			f = f.filter(
				(c) =>
					c.codigo?.toLowerCase().includes(s) ||
					c.descripcion?.toLowerCase().includes(s) ||
					c.codigoContrato?.toLowerCase().includes(s) ||
					c.descripcionContrato?.toLowerCase().includes(s) ||
					c.actividadContrato?.toLowerCase().includes(s) ||
					c.tipo_Instalacion?.toLowerCase().includes(s) ||
					c.responsable_Planta_Nombre?.toLowerCase().includes(s),
			);
		}
		this.filtered = f;
		this.page = 1;
	}

	// KPIs de resumen (display, derivados de los datos ya cargados).
	get kpiTotal(): number {
		return this.centros.length;
	}
	get kpiActive(): number {
		return this.centros.filter((c) => !!c.activo).length;
	}
	get kpiInactive(): number {
		return this.centros.filter((c) => !c.activo).length;
	}
	get kpiTypes(): number {
		return this.instalacionOptions.length;
	}

	// Paginación (común a tabla y tarjetas)
	get totalPages(): number {
		return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
	}
	get paged(): CentroProcedure[] {
		const start = (this.page - 1) * this.pageSize;
		return this.filtered.slice(start, start + this.pageSize);
	}
	goToPage(p: number): void {
		if (p >= 1 && p <= this.totalPages) {
			this.page = p;
		}
	}

	// ── Exportación a Excel (menú Utilidades) ────────────────────────────────
	isExportingExcel = false;

	/**
	 * Exporta a Excel lo que el usuario está viendo: el dataset FILTRADO
	 * completo (no la página visible) con todas las columnas del listado.
	 * Se excluyen edicionCentro/esResponsable: son flags de permisos del
	 * usuario que consulta, no datos del centro.
	 */
	async exportToExcel(): Promise<void> {
		if (this.isExportingExcel || this.filtered.length === 0) {
			return;
		}
		this.isExportingExcel = true;
		try {
			const t = (key: string) =>
				this.translateService.instant(key) as string;
			const context: { label: string; value: string }[] = [];
			if (this.searchText.trim()) {
				context.push({
					label: t("lst.export.ctx.search"),
					value: this.searchText.trim(),
				});
			}
			if (this.selectedInstalacion) {
				context.push({
					label: t("lst.filter.type"),
					value: this.selectedInstalacion,
				});
			}
			if (this.selectedActividad) {
				context.push({
					label: t("lst.filter.activity"),
					value: this.selectedActividad,
				});
			}
			if (this.selectedActivo) {
				context.push({
					label: t("lst.filter.status"),
					value: t(
						this.selectedActivo === "true"
							? "lst.col.active"
							: "lst.col.inactive",
					),
				});
			}
			await this.excelExport.export(
				this.filtered as unknown as Record<string, unknown>[],
				{
					fileName: t("header.menu.sites"),
					exclude: ["edicionCentro", "esResponsable"],
					context,
				},
			);
		} catch {
			this.notificationService.showError(
				this.translateService.instant("lst.export.error"),
			);
		} finally {
			this.isExportingExcel = false;
		}
	}

	onView(c: CentroProcedure): void {
		if (c.id) {
			this.router.navigate(["/sites", c.id]);
		}
	}

	/** ¿El usuario puede abrir la ficha de contrato? (capability, no scope del elemento). */
	get canViewContract(): boolean {
		return this.userProfileStore.hasPolicy(Policies.Contrato_View);
	}

	/**
	 * Navega al detalle del contrato del centro (chip de la columna Contrato). Para la
	 * propagación para no disparar también la navegación de la fila (detalle del centro).
	 * Si el perfil no tiene capacidad de ver contratos, avisa en vez de navegar (evitar el
	 * rebote a la home del AuthGuard de /contracts).
	 */
	onViewContract(c: CentroProcedure, event: MouseEvent): void {
		event.stopPropagation();
		if (!c.contratoId) {
			return;
		}
		if (!this.canViewContract) {
			this.notificationService.showInfo(
				this.translateService.instant("lst.contract.noAccess"),
			);
			return;
		}
		this.router.navigate(["/contracts", c.contratoId]);
	}

	trackById(index: number, c: CentroProcedure): number {
		return c.id || index;
	}

	/** Código corto PERSISTIDO del centro (ADR-0016): fijado en el alta, inmutable. */
	codigoCorto(c: CentroProcedure): string | null {
		return c.codigoCorto ?? null;
	}
}
